// app/javascript/utils/whenPageIdle.ts
//
// Resolve once the page has finished loading and the main thread has a quiet
// moment. Use it for third-party scripts nobody needs during first paint (e.g.
// Google One Tap) so they stop competing with the page's own JS while it boots.

export interface PageIdleEnv {
  readyState: () => DocumentReadyState;
  onLoad: (callback: () => void) => void;
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => unknown;
  setTimeout: (callback: () => void, ms: number) => unknown;
}

export interface WhenPageIdleOptions {
  /** Longest we wait for an idle period after load before running anyway (ms). */
  idleTimeout?: number;
  /** Delay after load on browsers without requestIdleCallback (iOS Safari) (ms). */
  fallbackDelay?: number;
  env?: PageIdleEnv;
}

export const DEFAULT_IDLE_TIMEOUT_MS = 3000;
export const DEFAULT_FALLBACK_DELAY_MS = 1000;

function browserEnv(): PageIdleEnv {
  return {
    readyState: () => document.readyState,
    onLoad: (callback) => window.addEventListener('load', callback, { once: true }),
    requestIdleCallback: typeof window.requestIdleCallback === 'function'
      ? (callback, options) => window.requestIdleCallback(callback, options)
      : undefined,
    setTimeout: (callback, ms) => window.setTimeout(callback, ms),
  };
}

export function whenPageIdle(options: WhenPageIdleOptions = {}): Promise<void> {
  const env = options.env ?? browserEnv();
  const idleTimeout = options.idleTimeout ?? DEFAULT_IDLE_TIMEOUT_MS;
  const fallbackDelay = options.fallbackDelay ?? DEFAULT_FALLBACK_DELAY_MS;

  return new Promise<void>((resolve) => {
    const afterLoad = () => {
      if (env.requestIdleCallback) {
        env.requestIdleCallback(() => resolve(), { timeout: idleTimeout });
      } else {
        env.setTimeout(() => resolve(), fallbackDelay);
      }
    };

    if (env.readyState() === 'complete') {
      afterLoad();
    } else {
      env.onLoad(afterLoad);
    }
  });
}
